'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const headlineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headlineRef?.current;
    if (!el) return;
    el?.classList?.remove('reveal-hidden');
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="/assets/images/kym-products/kym-hero.jpg"
          alt="SweetSkyn by Kym natural body butter collection — handcrafted in small batches"
          fill
          priority
          sizes="100vw"
          className="object-cover object-center" />
        
        {/* Gradient scrim — light text on dark env */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20" />
        {/* Nav-area tint — brown-wash gives the transparent navbar enough contrast
            for white text. Fades out by ~128px so it only affects the top band. */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#3b2417]/55 via-[#3b2417]/25 to-transparent pointer-events-none" />
      </div>
      {/* Content — bottom anchored */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-20 pt-40 w-full">
        <div
          ref={headlineRef}
          className="reveal reveal-hidden"
          style={{ animationDelay: '0.1s' }}>
          
          <span className="inline-block mb-6 px-4 py-1.5 border border-white/25 text-white/80 text-[10px] uppercase tracking-[0.35em] font-medium backdrop-blur-sm">
            Certified Skin Care Coach
          </span>

          <h1 className="text-hero text-white mb-6 max-w-4xl">
            Melanin magic <br />
            in every{' '}
            <span className="text-accent font-light"><span className="font-sans">J</span>ar</span>
          </h1>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-8">
            <Link
              href="/products"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-primary text-xs font-semibold uppercase tracking-widest hover:bg-accent hover:text-accent-foreground transition-all duration-300">
              
              Shop the Collection
              <span className="text-base leading-none">→</span>
            </Link>
            <Link
              href="/products"
              className="inline-flex items-center gap-3 px-8 py-4 border border-white/40 text-white text-xs font-semibold uppercase tracking-widest hover:bg-white/10 transition-all duration-300 backdrop-blur-sm">

              Explore Body Butters
            </Link>
          </div>
        </div>

        {/* Bottom stats row */}
        <div className="mt-16 pt-8 border-t border-white/15 flex flex-wrap gap-8 md:gap-16">
          {[
          { value: '8', label: 'Signature Scents' },
          { value: '100%', label: 'Natural Ingredients' },
          { value: 'Free', label: 'Shipping Over J$5,000' }]?.
          map((stat) =>
          <div key={stat?.label}>
              <p className="text-2xl md:text-3xl font-display font-light text-white">{stat?.value}</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/60 mt-1">{stat?.label}</p>
            </div>
          )}
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 md:right-10 z-10 flex flex-col items-center gap-2">
        <span className="text-[9px] uppercase tracking-[0.3em] text-white/50 rotate-90 origin-center mb-4">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>);

}